/**
 * @file services.ts
 * @path apps/website/src/data/services.ts
 * @description
 */

import { Activity, Service, ServiceCategory } from "@sirbastians/types";
// services.ts

import {
  capeTownSunriseHero,
  imageAirport,
  imageCapePoint,
  imageLionsHead,
  imagePrivate,
  imageTableMountain,
  imageWine,
  shuttle4,
} from "@/assets/images";
import { activitiesData } from "./activities"; // Import Activity type and data

// Find an activity by its slug from the activitiesData array
const findActivity = (slug: string): Activity | undefined =>
  activitiesData.find((a) => a.slug === slug);

export const services: Service[] = [
  /*{
    id: 0,
    slug: "test-service",
    title: "Test Service",
    category: "Transport",
    status: "unavailable",
    tags: ["Test"],
    shortDescription: "This is a test experience.",
    longDescription: "This is a test service for development purposes.",
    inclusions: ["Test inclusion 1", "Test inclusion 2"],
    exclusions: [],
    highlights: [],
    relatedActivities: [],
    includedActivities: [],
    optionalActivities: [],
    standardVehicle: {
      id: "standard-shuttle",
      name: "Standard Shuttle",
      maxPassengers: 20,
      description: "Comfortable air-conditioned shuttle",
    },
    gallery: {
      primary: shuttle3,
      additional: [],
      altText: "A test service image for validation.",
    },
    packageConfig: {
      description: "Test package configuration for validation.",
      maxIncludedStops: 2,
      extraStopPrice: 1,
      passengerConfig: {
        minAdults: 1,
        adultPrice: 2,
        childPrice: 1,
        infantPrice: 0,
        ageRanges: {
          infant: "0-2 years",
          child: "2-12 years",
          adult: "12+ years",
        },
      },
    },
    audience: ["Testing"],
    details: {
      quickInfo: [
        { icon: "AlarmFill", label: "Duration", value: "Test Duration" },
        { icon: "PeopleFill", label: "Capacity", value: "Test Capacity" },
        { icon: "GeoAltFill", label: "Area", value: "Test Area" },
      ],
      itinerary: [
        {
          title: "Test Itinerary Step",
          description: "This is a step in the test itinerary.",
        },
      ],
    },
    pricing: {
      amount: 2,
      type: "fixed",
      options: [
        {
          label: "Test Price",
          price: "R2",
          description: "per person",
        },
      ],
    },
  },*/
  {
    id: 1,
    slug: "uwc-cput-student-shuttle", // <-- UPDATED (from Part 2)
    title: "UWC & CPUT Student Shuttle", // <-- UPDATED (from Part 2, H1 recommendation)
    category: "Transport",
    status: "active",
    tags: [
      "Students",
      "Daily",
      "Affordable",
      "University",
      "Grassy Park",
      "Ottery",
      "CPUT",
      "UWC",
      "Bellville",
    ], // <-- UPDATED (with keywords)
    shortDescription:
      "End the multi-taxi commute. Get our safe, direct student shuttle from Grassy Park / Ottery to UWC & CPUT Bellville. 6 daily trips. Book on our app from R55.", // <-- UPDATED (from Part 2, Meta Desc. recommendation)
    longDescription:
      "Tired of the multi-taxi commute just to get to campus? The daily commute from Grassy Park / Ottery to UWC or CPUT Bellville can be a stressful, multi-step journey. We fixed it.\n\nSir Bastians offers a dedicated student shuttle service designed for the daily student commute. Our service provides a direct, safe, and reliable transport solution from Grassy Park / Ottery to the UWC and CPUT Bellville campuses.\n\nYour Hassle-Free Daily Commute:\nForget the uncertainty of public transport. Our professional drivers and comfortable shuttles run 6 times daily, operating on a fixed schedule. We have pickup points in Grassy Park (Shell Garage at 5th Ave/Victoria Rd) and Ottery. and drop you off directly on campus. With our 15-minute grace period, you have peace of mind, and at rates starting from just R55, it's the most affordable and efficient way to get to your classes.\n\nBook your one-way or round-trip journey in minutes via our dedicated student app. Secure your seat and make your daily student transport to UWC and CPUT reliable.", // <-- UPDATED (from Part 2, Full Rewrite)
    externalBookingUrl: "https://app.sirbastians.co.za",
    routes: [
      {
        name: "Grassy Park Shell Garage → UWC/CPUT",
        status: "active",
        description: "Corner of 5th Avenue and Victoria Road, Grassy Park",
      },
      {
        name: "Ottery Engen Garage → UWC/CPUT",
        status: "active",
        description: "Ottery Rd, Ottery",
      },
    ],
    inclusions: [
      "Comfortable shuttle transport",
      "15-minute grace period on all departures",
      "Professional driver",
      "University campus drop-off",
      "Personal items and luggage",
    ],
    exclusions: ["Meals and refreshments"],
    highlights: [
      "6 Daily Departure Times",
      "Affordable Student Rates",
      "15-Minute Grace Period",
      "Direct Campus Access",
      "Book via Student App",
      "Solves the Multi-Taxi Commute", // <-- ADDED (from blueprint)
    ],
    cancellationPolicy:
      "Cancellations must be made at least 24 hours in advance through the student app. No-shows will not be refunded. Please arrive within the 15-minute grace period to ensure departure.",
    relatedActivities: [],
    includedActivities: [],
    optionalActivities: [],
    gallery: {
      primary: shuttle4,
      altText:
        "Sir Bastians student shuttle van for UWC and CPUT transport, servicing Grassy Park and Ottery.", // <-- ADDED (from Part 2)
      additional: [],
    },
    audience: [
      "UWC Students",
      "CPUT Students",
      "Daily Commuters",
      "Budget-Conscious Students",
    ],
    details: {
      quickInfo: [
        { icon: "AlarmFill", label: "Duration", value: "6 daily departures" },
        { icon: "PeopleFill", label: "Capacity", value: "UWC & CPUT Students" },
        {
          icon: "GeoAltFill",
          label: "Route",
          value: "Grassy Park / Ottery ↔ Universities",
        },
      ],
      itinerary: [
        {
          title: "Departure from Grassy Park / Ottery",
          description: "Sharp departure with 15-minute grace period",
        },
        {
          time: "8:35 AM",
          title: "Departure from Grassy Park / Ottery",
          description: "Sharp departure with 15-minute grace period",
        },
        {
          time: "12:00 PM",
          title: "Departure from Grassy Park / Ottery",
          description: "Midday service",
        },
        {
          time: "1:00 PM",
          title: "Departure from UWC",
          description: "Return trip to Grassy Park / Ottery",
        },
        {
          time: "3:20 PM",
          title: "Departure from UWC",
          description: "Sharp departure with 15-minute grace period",
        },
        {
          time: "5:00 PM",
          title: "Departure from UWC",
          description: "Final departure of the day",
        },
      ],
      faq: [
        // <-- ADDED (from Part 3, Table 3)
        {
          question:
            "How do I get from Grassy Park or Ottery to UWC or CPUT Bellville?",
          answer:
            "Our direct student shuttle is the easiest way. Instead of multiple taxis, our service takes you from our pickup points in Grassy Park and Ottery directly to the UWC and CPUT campuses.",
        },
        {
          question: "Is this shuttle service for both UWC and CPUT students?",
          answer:
            "Yes, our shuttle serves students for both the University of the Western Cape (UWC) and the Cape Peninsula University of Technology (CPUT) Bellville campus.",
        },
        {
          question: "Where are the pickup points?",
          answer:
            "We have pickup points in Grassy Park (Shell Garage, corner of 5th Ave and Victoria Rd) and Ottery. The exact Ottery location will be confirmed soon.",
        },
        {
          question: "What is the daily schedule?",
          answer:
            "We have 6 daily departures to fit your class schedule, starting at 6:35 AM from Grassy Park / Ottery and 1:00 PM from UWC. (See the full itinerary above for all times).",
        },
        {
          question: "What happens if I'm running late?",
          answer:
            "We offer a 15-minute grace period on all departures. Please arrive within this window to ensure you don't miss your ride.",
        },
        {
          question: "How much does the shuttle cost?",
          answer:
            "A one-way trip is R55, and a round trip (both ways) is R110. You can book and pay securely via our student app.",
        },
        {
          question: "How do I book my trip?",
          answer:
            "All bookings are managed through our student app for convenience. You can download it at app.sirbastians.co.za.",
        },
      ],
    },
    pricing: {
      amount: 55,
      type: "from",
      options: [
        {
          label: "One Way",
          price: "R55",
          description: "Pickup to University OR University to Drop-off",
        },
        {
          label: "Both Ways (Round Trip)",
          price: "R110",
          description: "Full round trip service for the day",
        },
      ],
    },
  },
  {
    id: 2,
    slug: "airport-transfers",
    title: "Airport Transfers",
    category: "Transport",
    status: "active",
    tags: ["Private", "Reliable", "24/7", "Cape Town International"],
    shortDescription:
      "Reliable and comfortable transfers to and from Cape Town International Airport.",
    longDescription:
      "Allow us to safely and comfortably transport you from Cape Town International Airport to your preferred destination. Our qualified PDP driver will be waiting for you in the arrivals terminal with a welcome board with your name and will lead you to your private vehicle while assisting you with your luggage. We also offer return transfers. Rates are based on zones: Zone 1 is a +/- 30km radius from the airport, while Zone 2 covers areas like Franschhoek, Stellenbosch, and Simons Town (+/- 80km). An after-hours surcharge applies.",
    estimatedDuration: 60, // 1 hour base transfer time
    inclusions: [
      "Private air-conditioned vehicle",
      "Professional PDP driver",
      "Welcome board service",
    ],
    exclusions: [
      "After-hours surcharge",
      "Gratuity",
      "Baby Car Seat (R350)",
      "Trailer (R450)",
    ],
    highlights: [
      "Meet & Greet Service in Arrivals",
      "Luggage Assistance",
      "Direct, Private Transfer",
      "Sedans, Minibuses, and Luxury Cars Available",
    ],
    relatedActivities: [],
    includedActivities: [],
    optionalActivities: [],
    packageConfig: {
      description: "Direct airport transfer with pickup and drop-off included",
      maxIncludedStops: 2,
      extraStopPrice: 250,
    },
    standardVehicle: {
      id: "standard-shuttle",
      name: "Standard Shuttle",
      maxPassengers: 20,
      description: "Comfortable air-conditioned shuttle",
    },
    cancellationPolicy:
      "A 50% fee applies for cancellations 3 days prior; a 100% fee applies for cancellations within 24 hours.",
    route: {
      type: "point-to-point",
      stops: [
        {
          id: "pickup",
          name: "Pickup: Cape Town International Airport",
          address: "Cape Town International Airport",
          isEditable: true,
        },
        {
          id: "dropoff",
          name: "Drop-off: Your Destination",
          address: "",
          isEditable: true,
        },
      ],
    },
    gallery: { primary: imageAirport, additional: [] },
    audience: ["Solo Travelers", "Families", "Corporate Clients", "Groups"],
    details: {
      quickInfo: [
        { icon: "AlarmFill", label: "Duration", value: "Varies" },
        { icon: "PeopleFill", label: "Capacity", value: "1-22+ passengers" },
        { icon: "GeoAltFill", label: "Area", value: "Cape Town & Surrounds" },
      ],
    },
    pricing: {
      amount: 750,
      type: "from",
      options: [
        { label: "1-2 Pax (Zone 1)", price: "R750", description: "per group" },
        {
          label: "3-5 Pax (Zone 1)",
          price: "R1,000",
          description: "per group",
        },
        {
          label: "6-9 Pax (Zone 1)",
          price: "R1,400",
          description: "per group",
        },
        {
          label: "10-13 Pax (Zone 1)",
          price: "R1,750",
          description: "per group",
        },
        {
          label: "13-22 Pax (Zone 1)",
          price: "R3,000",
          description: "per group",
        },
        {
          label: "After Hours Surcharge",
          price: "from R200",
          description: "per trip",
        },
      ],
    },
  },
  {
    id: 3,
    slug: "private-vehicle-hire",
    title: "Private Vehicle Hire",
    category: "Transport",
    status: "active",
    tags: ["Flexible", "Personalized", "Half-Day", "Full-Day"],
    shortDescription:
      "Your personal driver for a half-day or full-day of bespoke travel.",
    longDescription:
      "In need of a private shuttle? Whether it be exploring the city or hopping from meeting to meeting, we have your transport needs covered! All transfers are completed with a qualified PDP driver.",
    estimatedDuration: 240, // 4 hours for half-day (can be 480 for full-day)
    inclusions: [
      "Private vehicle & qualified PDP driver",
      "Fuel for specified duration/radius",
    ],
    exclusions: [
      "All entry fees, meals, and personal expenses",
      "Gratuity",
      "Baby Car Seat (R350)",
      "Trailer (R450)",
    ],
    highlights: [
      "Complete Flexibility",
      "Personalized Itinerary",
      "Qualified PDP Driver",
      "Ideal for business or leisure",
    ],
    relatedActivities: [
      findActivity("kirstenbosch-gardens"),
      findActivity("va-waterfront"),
    ].filter(Boolean) as Activity[],
    includedActivities: [],
    optionalActivities: [
      findActivity("table-mountain-hikes"),
      findActivity("kirstenbosch-gardens"),
      findActivity("stellenbosch-wine-tours"),
      findActivity("cape-point-scenic-tours"),
      findActivity("boulders-beach-penguins"),
      findActivity("shark-cage-diving"),
      findActivity("va-waterfront"),
    ].filter(Boolean) as Activity[],
    packageConfig: {
      description: "Flexible itinerary - add activities as needed",
    },
    standardVehicle: {
      id: "standard-shuttle",
      name: "Standard Shuttle",
      maxPassengers: 20,
      description: "Comfortable air-conditioned shuttle",
    },
    cancellationPolicy:
      "A 50% fee applies for cancellations 3 days prior; a 100% fee applies for cancellations within 24 hours.",
    durationOptions: [
      { id: "half-day", label: "Half Day (4 hours)" },
      { id: "full-day", label: "Full Day (8 hours)" },
    ],
    route: {
      type: "point-to-point",
      stops: [
        {
          id: "pickup",
          name: "Your Pickup Location",
          address: "",
          isEditable: true,
        },
        {
          id: "dropoff",
          name: "Your Drop-off Location",
          address: "",
          isEditable: true,
        },
      ],
    },
    gallery: { primary: imagePrivate, additional: [] },
    audience: ["Business Travelers", "Custom Itineraries", "Special Occasions"],
    details: {
      quickInfo: [
        { icon: "AlarmFill", label: "Duration", value: "Half or Full Day" },
        { icon: "PeopleFill", label: "Capacity", value: "1-22+ passengers" },
      ],
      itinerary: [
        { time: "08:30 – 12:30", title: "Half Day (Morning)" },
        { time: "12:30 – 16:30", title: "Half Day (Afternoon)" },
        { time: "08:30 – 16:30", title: "Full Day" },
      ],
    },
    pricing: {
      amount: 2000,
      type: "from",
      options: [
        {
          label: "Half Day (1-2 Pax)",
          price: "R2,000",
          description: "per group",
        },
        {
          label: "Half Day (3-5 Pax)",
          price: "R2,500",
          description: "per group",
        },
        {
          label: "Half Day (6-9 Pax)",
          price: "R3,000",
          description: "per group",
        },
        {
          label: "Half Day (10-13 Pax)",
          price: "R3,500",
          description: "per group",
        },
        {
          label: "Full Day (1-2 Pax)",
          price: "R3,000",
          description: "per group",
        },
        {
          label: "Full Day (3-5 Pax)",
          price: "R3,750",
          description: "per group",
        },
        {
          label: "Full Day (6-9 Pax)",
          price: "R4,500",
          description: "per group",
        },
        {
          label: "Full Day (10-13 Pax)",
          price: "R5,250",
          description: "per group",
        },
        {
          label: "Extended Evening Driver",
          price: "R1,500",
          description: "add-on",
        },
      ],
    },
  },
  {
    id: 4,
    slug: "cape-point-peninsula-boulders-tour",
    title: "Cape Point & Peninsula Tour",
    subtitle: "Including Boulders Penguin Beach",
    category: "Tours",
    status: "active",
    tags: ["Full-Day", "Scenic", "Nature", "Wildlife"],
    shortDescription:
      "A breathtaking journey to the tip of Africa to see dramatic cliffs and charming penguins.",
    longDescription:
      "Welcome to an exhilarating journey through Cape Town's breathtaking landscapes and diverse wildlife. Join us on a guided tour as we explore the majestic Cape Point Peninsula and visit the charming Boulders Penguin Beach, where nature and beauty converge in perfect harmony.\n\nOur adventure begins with a scenic drive along the Atlantic seaboard, passing through picturesque coastal villages and pristine beaches. Marvel at the rugged cliffs and panoramic views of the Atlantic Ocean from Chapman's Peak Drive, one of the world's most scenic routes.\n\nArriving at the Cape of Good Hope Nature Reserve, immerse yourself in the natural beauty of this UNESCO World Heritage Site. Explore the rugged coastline and encounter native wildlife, including ostriches, baboons, and the elusive Cape Mountain zebra. Hike or take a funicular ride to the iconic Cape Point Lighthouse, where the Atlantic and Indian Oceans meet.\n\nYou will also visit Boulders Beach, a sanctuary for the adorable African penguins and observe them in their natural habitat.",
    estimatedDuration: 480, // 8 hours full-day tour
    inclusions: [
      "Door to Door Collection",
      "English Speaking Qualified Tour Guide",
      "Air-Conditioned Private Transport",
      "Entrance fee into Cape Point",
      "Entrance fee into Boulders Beach",
    ],
    exclusions: [
      "Lunch",
      "Gratuities",
      "Seal Island Boat Trip (+/- R100)",
      "Cape Point Funicular (+/- R80)",
    ],
    highlights: [
      "Scenic drive along Chapman's Peak Drive",
      "Explore Cape of Good Hope Nature Reserve",
      "Visit the Boulders Beach Penguin Colony",
      "Reach the Cape Point Lighthouse",
    ],
    relatedActivities: [
      findActivity("cape-point-scenic-tours"),
      findActivity("boulders-beach-penguins"),
    ].filter(Boolean) as Activity[],
    includedActivities: [
      findActivity("cape-point-scenic-tours"),
      findActivity("boulders-beach-penguins"),
    ].filter(Boolean) as Activity[],
    optionalActivities: [
      findActivity("hout-bay-seal-island"),
      findActivity("cape-point-funicular"),
    ].filter(Boolean) as Activity[],
    packageConfig: {
      description:
        "Base tour includes Cape Point & Boulders Beach entrance fees",
      maxIncludedStops: 5,
      extraStopPrice: 300,
      passengerConfig: {
        minAdults: 2,
        adultPrice: 3000,
        childPrice: 1500,
        infantPrice: 0,
        ageRanges: {
          infant: "0-2 years",
          child: "2-12 years",
          adult: "12+ years",
        },
      },
    },
    standardVehicle: {
      id: "standard-tour-vehicle",
      name: "Standard Tour Vehicle",
      maxPassengers: 20,
      description: "Comfortable air-conditioned tour vehicle",
    },
    vipOptions: [
      {
        id: "mercedes-c-class",
        label: "Mercedes C-Class",
        description: "Luxury 3-seater sedan",
        maxPassengers: 3,
        price: 2000,
      },
      {
        id: "mercedes-v-class",
        label: "Mercedes V-Class",
        description: "Premium 6-seater van",
        maxPassengers: 6,
        price: 5000,
      },
    ],
    cancellationPolicy:
      "A 50% fee applies for cancellations 3 days prior; a 100% fee applies for cancellations within 24 hours.",
    route: {
      type: "fixed-itinerary",
      stops: [
        {
          id: "stop1",
          name: "Your Pickup Location",
          address: "",
          isEditable: true,
        },
        {
          id: "stop2",
          name: "Boulders Penguin Colony",
          address: "Kleintuin Rd, Simon's Town",
          isEditable: false,
        },
        {
          id: "stop3",
          name: "Cape Point Nature Reserve",
          address: "Cape Point Rd, Cape Town",
          isEditable: false,
        },
        {
          id: "stop4",
          name: "Chapman's Peak Drive",
          address: "Chapman's Peak Dr, Hout Bay",
          isEditable: false,
        },
        {
          id: "stop5",
          name: "Your Drop-off Location",
          address: "",
          isEditable: true,
        },
      ],
    },
    gallery: { primary: imageCapePoint, additional: [] },
    audience: [
      "First-time Visitors",
      "Families",
      "Nature Lovers",
      "Photographers",
    ],
    details: {
      quickInfo: [
        { icon: "AlarmFill", label: "Duration", value: "Full Day" },
        { icon: "PeopleFill", label: "Group Size", value: "Min. 2 adults" },
      ],
    },
    pricing: {
      amount: 3000,
      type: "from",
      options: [
        { label: "Adult", price: "R3,000", description: "per person" },
        { label: "Child (2-12)", price: "R1,500", description: "per person" },
        {
          label: "VIP Mercedes C-Class (3-seater)",
          price: "R2,000",
          description: "vehicle upgrade",
        },
      ],
    },
  },
  {
    id: 5,
    slug: "constantia-steenberg-wine-tour",
    title: "Constantia & Steenberg",
    subtitle: "Wine Tasting Tour",
    category: "Tours",
    status: "active",
    tags: ["Full-Day", "Luxury", "Wine Tasting", "History"],
    shortDescription:
      "A luxurious wine tasting journey through the picturesque Constantia Valley.",
    longDescription:
      "Looking for the ultimate wine experience in Cape Town? Embark on a Luxurious Wine Tasting Journey through the picturesque Constantia Valley and Steenberg area of Cape Town. Prepare to indulge in exquisite wines amidst stunning vineyard landscapes, where centuries of winemaking tradition meet modern sophistication.\n\nConstantia Uitsig Wine Estate: Here you'll be greeted by sweeping vineyard views and the estate's charming Cape Dutch architecture. A historic estate nestled in the heart of the Constantia Valley near Cape Town.\n\nSteenberg Wine Estate: Venture to Steenberg, one of Cape Town's premier wine estates located in the foothills of the Constantiaberg Mountains. Steenberg boasts a rich history dating back to 1682 and produces a diverse range of wines, including Sauvignon Blanc, Merlot, and their signature Méthode Cap Classique sparkling wines.\n\nGroot Constantia: Visit the esteemed and oldest wine estate in South Africa dating back to 1685. Sample a selection of award-winning wines in a picturesque setting, surrounded by lush greenery and Cape Dutch architecture.\n\nConstantia Glen: Located below Constantia Nek is Constantia Glen, a boutique wine estate nestled in the scenic Constantia Valley near Cape Town. Prepare to be enchanted by panoramic vineyard views, exceptional wines, and warm hospitality.\n\nEagles Nest Wine Estate: Where the essence of Cape Town's winemaking heritage meets stunning natural beauty. Nestled in the secluded Constantia Valley, Eagles' Nest promises a wine tasting experience that combines exquisite wines with breathtaking landscapes.\n\nBeau Constantia: Embark on a scenic drive to Beau Constantia, where you'll be greeted by sweeping vistas of vineyards, mountains, and the shimmering Atlantic Ocean. Beau Constantia is renowned for its boutique wines, specializing in Sauvignon Blanc, Chardonnay, and Bordeaux-style blends.",
    estimatedDuration: 480, // 8 hours full-day wine tour
    inclusions: [
      "Door to Door Collection",
      "English Speaking Qualified Tour Guide",
      "Air-Conditioned Private Transport",
      "All Wine Estate Tasting (Standard option)",
    ],
    exclusions: ["Lunch", "Gratuities"],
    highlights: [
      "Visit Groot Constantia, SA's oldest wine estate (1685)",
      "Taste premium wines at Steenberg Estate (1682)",
      "Enjoy panoramic vineyard views",
      "Explore up to 6 unique and historic wine estates",
    ],
    relatedActivities: [findActivity("stellenbosch-wine-tours")].filter(
      Boolean,
    ) as Activity[],
    includedActivities: [findActivity("stellenbosch-wine-tours")].filter(
      Boolean,
    ) as Activity[],
    optionalActivities: [],
    packageConfig: {
      description: "All wine estate tastings included in base price",
      maxIncludedStops: 6,
      extraStopPrice: 400,
      passengerConfig: {
        minAdults: 2,
        adultPrice: 3500,
        childPrice: 1750,
        infantPrice: 0,
        ageRanges: {
          infant: "0-2 years",
          child: "2-12 years",
          adult: "12+ years",
        },
      },
    },
    standardVehicle: {
      id: "standard-tour-vehicle",
      name: "Standard Tour Vehicle",
      maxPassengers: 20,
      description: "Comfortable air-conditioned tour vehicle",
    },
    vipOptions: [
      {
        id: "mercedes-c-class",
        label: "Mercedes C-Class",
        description: "Luxury 3-seater sedan",
        maxPassengers: 3,
        price: 2000,
      },
      {
        id: "mercedes-v-class",
        label: "Mercedes V-Class",
        description: "Premium 6-seater van",
        maxPassengers: 6,
        price: 5000,
      },
    ],
    cancellationPolicy:
      "A 50% fee applies for cancellations 3 days prior; a 100% fee applies for cancellations within 24 hours.",
    route: {
      type: "fixed-itinerary",
      stops: [
        {
          id: "stop1",
          name: "Your Pickup Location",
          address: "",
          isEditable: true,
        },
        {
          id: "stop2",
          name: "Groot Constantia",
          address: "Groot Constantia Rd, Constantia",
          isEditable: false,
        },
        {
          id: "stop3",
          name: "Steenberg Vineyards",
          address: "Steenberg Rd, Tokai",
          isEditable: false,
        },
        {
          id: "stop4",
          name: "Beau Constantia",
          address: "1043 Constantia Main Rd, Constantia",
          isEditable: false,
        },
        {
          id: "stop5",
          name: "Your Drop-off Location",
          address: "",
          isEditable: true,
        },
      ],
    },
    gallery: { primary: imageWine, additional: [] },
    audience: ["Wine Connoisseurs", "Couples", "Gourmet Travelers"],
    details: {
      quickInfo: [
        { icon: "AlarmFill", label: "Duration", value: "Approx. 8 Hours" },
        { icon: "PeopleFill", label: "Group Size", value: "Min. 2 adults" },
      ],
      itinerary: [
        { time: "08:30", title: "Hotel Pick-up" },
        {
          title: "Constantia Uitsig Wine Estate",
          description:
            "Sweeping vineyard views and charming Cape Dutch architecture",
        },
        {
          title: "Steenberg Wine Estate",
          description: "Premium wine estate dating back to 1682",
        },
        {
          title: "Groot Constantia Wine Estate",
          description: "South Africa's oldest wine estate (1685)",
        },
        {
          title: "Constantia Glen Wine Estate",
          description: "Boutique estate with panoramic views",
        },
        {
          title: "Lunch (Own Account)",
          description: "Enjoy fine dining at one of the estates",
        },
        {
          title: "Eagles Nest Wine Estate",
          description: "Secluded valley location with stunning landscapes",
        },
        {
          title: "Beau Constantia",
          description: "Final stop with ocean and mountain vistas",
        },
      ],
    },
    pricing: {
      amount: 2750,
      type: "from",
      options: [
        { label: "Adult", price: "R2,750", description: "per person" },
        { label: "Child (2-12)", price: "R1,375", description: "per person" },
      ],
    },
  },
  {
    id: 6,
    slug: "big-5-safari-tour",
    title: "Big 5 Safari",
    subtitle: "Full Day Luxury Tour",
    category: "Tours",
    status: "active",
    tags: ["Full-Day", "Safari", "Wildlife", "Big 5"],
    shortDescription:
      "Explore wildlife up close at a private game reserve near Cape Town.",
    longDescription:
      "Looking to explore wildlife up close in their natural habitat? Visiting Aquila Private Game Reserve in South Africa offers a fantastic safari experience, especially for those looking to explore wildlife in a convenient location near Cape Town. Aquila is home to the Big Five (the lion, elephant, leopard, rhino, and buffalo) as well as other wildlife such as zebras, giraffes and various antelope species.",
    estimatedDuration: 600, // 10 hours including travel
    inclusions: [
      "Door to Door Collection",
      "English Speaking Qualified Tour Guide",
      "Air-Conditioned Private Transport",
      "Breakfast",
      "Entrance fee into Game Reserve",
      "2-3 hour Traditional Safari Game Drive",
      "Visit to the Animal Rescue Centre",
      "Full Buffet Lunch",
      "Use of all the Reserves Facilities",
    ],
    exclusions: ["Gratuities"],
    highlights: [
      "Opportunity to see the Big Five in their natural habitat",
      "2-3 hour traditional safari game drive with expert guides",
      "Visit to the Animal Rescue Centre",
      "Breakfast and Full Buffet Lunch included",
      "Full use of all reserve facilities",
    ],
    relatedActivities: [],
    includedActivities: [],
    optionalActivities: [],
    packageConfig: {
      description: "All-inclusive safari experience with meals and game drive",
      maxIncludedStops: 3,
      extraStopPrice: 0,
      passengerConfig: {
        minAdults: 2,
        adultPrice: 3900,
        childPrice: 1950,
        infantPrice: 0,
        ageRanges: {
          infant: "0-2 years",
          child: "2-12 years",
          adult: "12+ years",
        },
      },
    },
    standardVehicle: {
      id: "standard-tour-vehicle",
      name: "Standard Tour Vehicle",
      maxPassengers: 20,
      description: "Comfortable air-conditioned tour vehicle",
    },
    vipOptions: [
      {
        id: "mercedes-c-class",
        label: "Mercedes C-Class",
        description: "Luxury 3-seater sedan",
        maxPassengers: 3,
        price: 2000,
      },
      {
        id: "mercedes-v-class",
        label: "Mercedes V-Class",
        description: "Premium 6-seater van",
        maxPassengers: 6,
        price: 5000,
      },
    ],
    cancellationPolicy:
      "A 50% fee applies for cancellations 3 days prior; a 100% fee applies for cancellations within 24 hours.",
    route: {
      type: "point-to-point",
      stops: [
        {
          id: "pickup",
          name: "Your Pickup Location",
          address: "",
          isEditable: true,
        },
        {
          id: "destination",
          name: "Aquila Private Game Reserve",
          address: "R46, Touws River",
          isEditable: false,
        },
        {
          id: "dropoff",
          name: "Your Drop-off Location",
          address: "",
          isEditable: true,
        },
      ],
    },
    gallery: { primary: capeTownSunriseHero, additional: [] },
    audience: ["Wildlife Enthusiasts", "Families", "First-time Safari Goers"],
    details: {
      quickInfo: [
        { icon: "AlarmFill", label: "Duration", value: "Full Day" },
        { icon: "PeopleFill", label: "Group Size", value: "Min. 2 adults" },
        { icon: "GeoAltFill", label: "Location", value: "Aquila Game Reserve" },
      ],
    },
    pricing: {
      amount: 3900,
      type: "from",
      options: [
        { label: "Adult", price: "R3,900", description: "per person" },
        { label: "Child (2-12)", price: "R1,950", description: "per person" },
      ],
    },
  },
  {
    id: 7,
    slug: "4-day-garden-route-tour",
    title: "The Garden Route",
    subtitle: "4-Day Adventure",
    category: "Tours",
    status: "active",
    tags: ["Multi-Day", "Adventure", "Nature", "Safari"],
    shortDescription:
      "An outstanding 4-day tour covering Oudtshoorn, Knysna, Tsitsikamma, and Mossel Bay.",
    longDescription:
      "Experience a memorable four days along the remarkable Garden Route, perfect for nature lovers and adventure seekers. From the harbour town of Knysna, to the lush forest of the Tsitsikamma Nature Reserve, to the popular beaches of Plettenberg Bay and Mossel Bay as well as the ‘Ostrich Capital of the World’, Oudtshoorn.",
    inclusions: [
      "3 nights accommodation with dinner & breakfast",
      "All specified entrance fees (Cango Wildlife Ranch, Ostrich Farm, Cango Caves, etc.)",
      "Wine-tasting",
      "Safari tour at Garden Route Game Lodge",
    ],
    exclusions: [
      "Lunches",
      "Gratuities",
      "Optional activities like The Elephant Park, Monkeyland & Birds of Eden",
    ],
    highlights: [
      "Visit an ostrich farm and the Cango Caves",
      "Visit Tsitsikamma National Park and Knysna",
      "Enjoy a Safari game drive in a private reserve",
      "Wine-tasting in the Robertson winelands",
    ],
    relatedActivities: [],
    includedActivities: [],
    optionalActivities: [],
    packageConfig: {
      description: "4-day multi-stop tour with all accommodations included",
      maxIncludedStops: 5,
      extraStopPrice: 0,
      passengerConfig: {
        minAdults: 2,
        adultPrice: 24000,
        childPrice: 14000,
        infantPrice: 0,
        ageRanges: {
          infant: "0-2 years",
          child: "2-12 years",
          adult: "12+ years",
        },
      },
    },
    standardVehicle: {
      id: "standard-tour-vehicle",
      name: "Standard Tour Vehicle",
      maxPassengers: 20,
      description: "Comfortable air-conditioned tour vehicle",
    },
    vipOptions: [
      {
        id: "mercedes-c-class",
        label: "Mercedes C-Class",
        description: "Luxury 3-seater sedan",
        maxPassengers: 3,
        price: 2000,
      },
      {
        id: "mercedes-v-class",
        label: "Mercedes V-Class",
        description: "Premium 6-seater van",
        maxPassengers: 6,
        price: 5000,
      },
    ],
    cancellationPolicy:
      "A 50% fee applies for cancellations 3 days prior; a 100% fee applies for cancellations within 24 hours.",
    route: {
      type: "fixed-itinerary",
      stops: [
        {
          id: "stop1",
          name: "Start: Cape Town",
          address: "Your pickup address in Cape Town",
          isEditable: true,
        },
        {
          id: "stop2",
          name: "Stop: Oudtshoorn",
          address: "Oudtshoorn, Western Cape",
          isEditable: false,
        },
        {
          id: "stop3",
          name: "Stop: Knysna",
          address: "Knysna, Western Cape",
          isEditable: false,
        },
        {
          id: "stop4",
          name: "Stop: Mossel Bay",
          address: "Mossel Bay, Western Cape",
          isEditable: false,
        },
        {
          id: "stop5",
          name: "End: Cape Town",
          address: "Your drop-off address in Cape Town",
          isEditable: true,
        },
      ],
    },
    gallery: { primary: imageLionsHead, additional: [] },
    audience: ["Adventure Seekers", "Nature Lovers", "Road Trippers"],
    details: {
      quickInfo: [
        {
          icon: "CalendarDateFill",
          label: "Duration",
          value: "4 Days / 3 Nights",
        },
        { icon: "PeopleFill", label: "Group Size", value: "Min. 2 adults" },
        { icon: "GeoAltFill", label: "Region", value: "The Garden Route" },
      ],
      itinerary: [
        {
          title: "Day 1: Cape Town → Oudtshoorn",
          description:
            "After collection from your hotel, travel to Oudtshoorn via the Robertson winelands with a wine-tasting stop. Visit Cango Wildlife Ranch (cheetah contact centre) and an Ostrich Farm. Dinner & overnight in Oudtshoorn.",
        },
        {
          title: "Day 2: Oudtshoorn → Knysna",
          description:
            "Breakfast, then visit the world-famous Cango Caves. Lunch at 'Kobus se gat'. Journey to Knysna and visit Knysna Heads. Optional: The Elephant Park. Overnight in Knysna.",
        },
        {
          title: "Day 3: Knysna → Tsitsikamma → Plettenberg Bay",
          description:
            "Breakfast, then visit Nature's Valley and Tsitsikamma Nature Reserve. Optional: Monkeyland & Birds of Eden. Visit Plettenberg Bay. Dinner & overnight in Knysna.",
        },
        {
          title: "Day 4: Mossel Bay → Cape Town",
          description:
            "Breakfast, explore Mossel Bay, then enjoy a safari tour at Garden Route Game Lodge. Return to Cape Town.",
        },
      ],
    },
    pricing: {
      amount: 24000,
      type: "from",
      options: [
        {
          label: "Adult (3-star)",
          price: "R24,000",
          description: "per person",
        },
        {
          label: "Adult (4-star)",
          price: "R30,000",
          description: "per person",
        },
        {
          label: "Adult (5-star)",
          price: "R40,000",
          description: "per person",
        },
        {
          label: "Child (3-star)",
          price: "R14,000",
          description: "per person (2-12 years)",
        },
        {
          label: "Child (4-star)",
          price: "R20,000",
          description: "per person (2-12 years)",
        },
        {
          label: "Child (5-star)",
          price: "R25,000",
          description: "per person (2-12 years)",
        },
        {
          label: "Trailer",
          price: "R450",
          description: "per day",
        },
        {
          label: "Baby Car Seat",
          price: "R350",
          description: "per day",
        },
      ],
    },
  },
  {
    id: 8,
    slug: "city-tour-robben-island-table-mountain",
    title: "Cape Town City Tour (Option 2)",
    subtitle: "Robben Island & Table Mountain",
    category: "Tours",
    status: "active",
    tags: ["City Tour", "History", "Culture", "Iconic Sights"],
    shortDescription:
      "A comprehensive experience of Cape Town's historical significance and natural beauty.",
    longDescription:
      "Enjoy some of the city’s highlights such as Robben Island, Table Mountain, Bo-Kaap, and the Castle of Good Hope in one day! This tour offers a profound and educational experience regarding South Africa's history, particularly its struggle against apartheid. After the 3-hour guided tour of Robben Island, take the cable car to the top of Table Mountain for breathtaking panoramic views of Cape Town.",
    inclusions: [
      "Door to Door Collection",
      "English Speaking Qualified Tour Guide",
      "Entrance fee into Table Mountain",
      "Entrance fee into Castle of Good Hope",
      "Entrance fee into Robben Island Boat and Tour",
    ],
    exclusions: ["Lunch", "Gratuities"],
    highlights: [
      "3-hour guided tour of Robben Island (Weather Permitting)",
      "Panoramic views from Table Mountain",
      "Visit the colourful Bo-Kaap neighborhood",
      "Explore the Castle of Good Hope",
    ],
    relatedActivities: [
      findActivity("table-mountain-hikes"),
      findActivity("va-waterfront"),
    ].filter(Boolean) as Activity[],
    includedActivities: [
      findActivity("table-mountain-hikes"),
      findActivity("va-waterfront"),
    ].filter(Boolean) as Activity[],
    optionalActivities: [
      findActivity("kirstenbosch-gardens"),
      findActivity("cape-point-scenic-tours"),
    ].filter(Boolean) as Activity[],
    packageConfig: {
      description:
        "City tour with Table Mountain & Robben Island entrance fees included",
      maxIncludedStops: 5,
      extraStopPrice: 300,
      passengerConfig: {
        minAdults: 2,
        adultPrice: 3500,
        childPrice: 1750,
        infantPrice: 0,
        ageRanges: {
          infant: "0-2 years",
          child: "2-12 years",
          adult: "12+ years",
        },
      },
    },
    standardVehicle: {
      id: "standard-tour-vehicle",
      name: "Standard Tour Vehicle",
      maxPassengers: 20,
      description: "Comfortable air-conditioned tour vehicle",
    },
    vipOptions: [
      {
        id: "mercedes-c-class",
        label: "Mercedes C-Class",
        description: "Luxury 3-seater sedan",
        maxPassengers: 3,
        price: 2000,
      },
      {
        id: "mercedes-v-class",
        label: "Mercedes V-Class",
        description: "Premium 6-seater van",
        maxPassengers: 6,
        price: 5000,
      },
    ],
    cancellationPolicy:
      "A 50% fee applies for cancellations 3 days prior; a 100% fee applies for cancellations within 24 hours.",
    route: {
      type: "fixed-itinerary",
      stops: [
        {
          id: "stop1",
          name: "Your Pickup Location",
          address: "",
          isEditable: true,
        },
        {
          id: "stop2",
          name: "Table Mountain Aerial Cableway",
          address: "Tafelberg Rd, Gardens",
          isEditable: false,
        },
        {
          id: "stop3",
          name: "Bo-Kaap",
          address: "Bo-Kaap, Cape Town",
          isEditable: false,
        },
        {
          id: "stop4",
          name: "V&A Waterfront (Robben Island Ferry)",
          address: "V&A Waterfront, Cape Town",
          isEditable: false,
        },
        {
          id: "stop5",
          name: "Your Drop-off Location",
          address: "",
          isEditable: true,
        },
      ],
    },
    gallery: { primary: imageTableMountain, additional: [] },
    audience: ["History Buffs", "First-time Visitors", "Culture Enthusiasts"],
    details: {
      quickInfo: [
        { icon: "AlarmFill", label: "Duration", value: "Full Day" },
        { icon: "PeopleFill", label: "Group Size", value: "Min. 2 adults" },
      ],
      itinerary: [
        { time: "08:30", title: "Hotel Pick-up" },
        {
          title: "3-hour tour of Robben Island",
          description: "Weather Permitting",
        },
        {
          title: "Bo-Kaap",
          description: "Explore the colourful historic neighbourhood",
        },
        { title: "Greenmarket Square - Lunch (Own Account)" },
        {
          title: "Castle of Good Hope",
          description: "South Africa's oldest colonial building",
        },
        {
          title: "Table Mountain",
          description: "Cable car to the summit for panoramic views",
        },
        { title: "Diamond Cutting Tour", description: "Time Permitting" },
      ],
    },
    pricing: {
      amount: 3500,
      type: "from",
      options: [
        { label: "Adult", price: "R3,500", description: "per person" },
        { label: "Child (2-12)", price: "R1,750", description: "per person" },
      ],
    },
  },
  {
    id: 9,
    slug: "city-tour-table-mountain-kirstenbosch",
    title: "Cape Town City Tour (Option 1)",
    subtitle: "Table Mountain & Kirstenbosch Gardens",
    category: "Tours",
    status: "active",
    tags: ["City Tour", "Nature", "Culture", "Iconic Sights"],
    shortDescription:
      "Experience Cape Town's natural wonders and botanical treasures in one day.",
    longDescription:
      "By combining Table Mountain with Kirstenbosch National Botanical Garden, you'll experience the natural wonders and botanical treasures that Cape Town has to offer, providing a perfect blend of adventure, relaxation, and education in one memorable tour.\n\nEnjoy all the city's highlights such as Table Mountain, Kirstenbosch Botanical Gardens, the Iziko Slave Lodge, and the lively V&A Waterfront in one day!",
    inclusions: [
      "Door to Door Collection",
      "English Speaking Qualified Tour Guide",
      "Entrance fee into Table Mountain",
      "Entrance fee into Iziko Slave Lodge",
      "Entrance fee into Kirstenbosch Gardens",
    ],
    exclusions: ["Lunch", "Gratuities"],
    highlights: [
      "Cable car ride to Table Mountain summit",
      "Visit Iziko Slave Lodge Museum",
      "Explore V&A Waterfront",
      "Discover Kirstenbosch Botanical Gardens",
      "Panoramic city and ocean views",
    ],
    relatedActivities: [
      findActivity("table-mountain-hikes"),
      findActivity("kirstenbosch-gardens"),
      findActivity("va-waterfront"),
    ].filter(Boolean) as Activity[],
    includedActivities: [
      findActivity("table-mountain-hikes"),
      findActivity("kirstenbosch-gardens"),
      findActivity("iziko-slave-lodge"),
      findActivity("va-waterfront"),
    ].filter(Boolean) as Activity[],
    optionalActivities: [],
    packageConfig: {
      description:
        "City tour with Table Mountain & Kirstenbosch entrance fees included",
      maxIncludedStops: 4,
      extraStopPrice: 300,
      passengerConfig: {
        minAdults: 2,
        adultPrice: 3500,
        childPrice: 1750,
        infantPrice: 0,
        ageRanges: {
          infant: "0-2 years",
          child: "2-12 years",
          adult: "12+ years",
        },
      },
    },
    standardVehicle: {
      id: "standard-tour-vehicle",
      name: "Standard Tour Vehicle",
      maxPassengers: 20,
      description: "Comfortable air-conditioned tour vehicle",
    },
    vipOptions: [
      {
        id: "mercedes-c-class",
        label: "Mercedes C-Class",
        description: "Luxury 3-seater sedan",
        maxPassengers: 3,
        price: 2000,
      },
      {
        id: "mercedes-v-class",
        label: "Mercedes V-Class",
        description: "Premium 6-seater van",
        maxPassengers: 6,
        price: 5000,
      },
    ],
    cancellationPolicy:
      "A 50% fee applies for cancellations 3 days prior; a 100% fee applies for cancellations within 24 hours.",
    route: {
      type: "fixed-itinerary",
      stops: [
        {
          id: "stop1",
          name: "Your Pickup Location",
          address: "",
          isEditable: true,
        },
        {
          id: "stop2",
          name: "Table Mountain Aerial Cableway",
          address: "Tafelberg Rd, Gardens",
          isEditable: false,
        },
        {
          id: "stop3",
          name: "Iziko Slave Lodge",
          address: "49 Adderley St, Cape Town City Centre",
          isEditable: false,
        },
        {
          id: "stop4",
          name: "V&A Waterfront",
          address: "V&A Waterfront, Cape Town",
          isEditable: false,
        },
        {
          id: "stop5",
          name: "Kirstenbosch Gardens",
          address: "Rhodes Dr, Newlands",
          isEditable: false,
        },
        {
          id: "stop6",
          name: "Your Drop-off Location",
          address: "",
          isEditable: true,
        },
      ],
    },
    gallery: { primary: imageTableMountain, additional: [] },
    audience: ["Nature Lovers", "First-time Visitors", "Families"],
    details: {
      quickInfo: [
        { icon: "AlarmFill", label: "Duration", value: "Full Day" },
        { icon: "PeopleFill", label: "Group Size", value: "Min. 2 adults" },
      ],
      itinerary: [
        { time: "08:30", title: "Hotel Pick-up" },
        {
          title: "Table Mountain",
          description: "Cable car to the summit for 360° views",
        },
        {
          title: "Iziko Slave Lodge Museum",
          description: "Learn about South Africa's history",
        },
        {
          title: "V&A Waterfront",
          description: "Optional lunch at your own account",
        },
        {
          title: "Kirstenbosch Gardens",
          description: "Explore world-renowned botanical gardens",
        },
      ],
    },
    pricing: {
      amount: 3500,
      type: "from",
      options: [
        { label: "Adult", price: "R3,500", description: "per person" },
        { label: "Child (2-12)", price: "R1,750", description: "per person" },
      ],
    },
  },
  {
    id: 10,
    slug: "franschhoek-stellenbosch-paarl-wine-tour",
    title: "Franschhoek, Stellenbosch & Paarl Wine Tour",
    category: "Tours",
    status: "active",
    tags: ["Full-Day", "Wine Tasting", "Cape Winelands", "Gourmet"],
    shortDescription:
      "A wine tasting tour through three renowned Cape Winelands regions.",
    longDescription:
      "A wine tasting tour through Franschhoek, Stellenbosch, and Paarl in the Cape Winelands of South Africa is a delightful journey through some of the country's most renowned wine-producing regions. Here's what you can expect: Scenic drives and scenery, a variety of wines, historical and cultural insights, food pairings and warm hospitality.\n\nFranschhoek is a quaint and picturesque town known for its French Huguenot heritage and its reputation as the 'Food and Wine Capital' of South Africa.\n\nStellenbosch is South Africa's second oldest town and is famous for its historic charm, oak-lined streets, and world-class wines.\n\nPaarl is the third oldest town in South Africa and boasts a mix of historical charm and natural beauty.",
    inclusions: [
      "Door to Door Collection",
      "English Speaking Qualified Tour Guide",
      "Air-Conditioned Private Transport",
      "All Wine Estate Tasting (Standard option)",
    ],
    exclusions: ["Lunch", "Gratuities"],
    highlights: [
      "Visit three iconic wine regions in one day",
      "Taste world-class wines across multiple estates",
      "Explore historic towns and scenic vineyard landscapes",
      "Experience French Huguenot heritage in Franschhoek",
    ],
    relatedActivities: [findActivity("stellenbosch-wine-tours")].filter(
      Boolean,
    ) as Activity[],
    includedActivities: [findActivity("stellenbosch-wine-tours")].filter(
      Boolean,
    ) as Activity[],
    optionalActivities: [],
    packageConfig: {
      description: "All wine estate tastings included in base price",
      maxIncludedStops: 6,
      extraStopPrice: 400,
      passengerConfig: {
        minAdults: 2,
        adultPrice: 3000,
        childPrice: 1500,
        infantPrice: 0,
        ageRanges: {
          infant: "0-2 years",
          child: "2-12 years",
          adult: "12+ years",
        },
      },
    },
    standardVehicle: {
      id: "standard-tour-vehicle",
      name: "Standard Tour Vehicle",
      maxPassengers: 20,
      description: "Comfortable air-conditioned tour vehicle",
    },
    vipOptions: [
      {
        id: "mercedes-c-class",
        label: "Mercedes C-Class",
        description: "Luxury 3-seater sedan",
        maxPassengers: 3,
        price: 2000,
      },
      {
        id: "mercedes-v-class",
        label: "Mercedes V-Class",
        description: "Premium 6-seater van",
        maxPassengers: 6,
        price: 5000,
      },
    ],
    cancellationPolicy:
      "A 50% fee applies for cancellations 3 days prior; a 100% fee applies for cancellations within 24 hours.",
    route: {
      type: "fixed-itinerary",
      stops: [
        {
          id: "stop1",
          name: "Your Pickup Location",
          address: "",
          isEditable: true,
        },
        {
          id: "stop2",
          name: "Franschhoek Wine Estate",
          address: "Franschhoek, Western Cape",
          isEditable: false,
        },
        {
          id: "stop3",
          name: "Stellenbosch Wine Estate",
          address: "Stellenbosch, Western Cape",
          isEditable: false,
        },
        {
          id: "stop4",
          name: "Paarl Wine Estate",
          address: "Paarl, Western Cape",
          isEditable: false,
        },
        {
          id: "stop5",
          name: "Your Drop-off Location",
          address: "",
          isEditable: true,
        },
      ],
    },
    gallery: { primary: imageWine, additional: [] },
    audience: ["Wine Connoisseurs", "Couples", "Gourmet Travelers"],
    details: {
      quickInfo: [
        {
          icon: "AlarmFill",
          label: "Duration",
          value: "Full Day (Approx. 8 Hours)",
        },
        { icon: "PeopleFill", label: "Group Size", value: "Min. 2 adults" },
      ],
      itinerary: [
        { time: "08:30", title: "Hotel Pick-up" },
        {
          title: "Franschhoek Wine Estates",
          description: "Visit wineries in the Food and Wine Capital",
        },
        {
          title: "Stellenbosch Wine Estates",
          description: "Oak-lined streets and world-class wines",
        },
        {
          title: "Lunch (Own Account)",
          description: "Enjoy gourmet dining in the Winelands",
        },
        {
          title: "Paarl Wine Estates",
          description: "Historic town with mountain views",
        },
      ],
    },
    pricing: {
      amount: 3000,
      type: "from",
      options: [
        { label: "Adult", price: "R3,000", description: "per person" },
        { label: "Child (2-12)", price: "R1,500", description: "per person" },
      ],
    },
  },
  {
    id: 11,
    slug: "west-coast-tour",
    title: "West Coast Tour",
    category: "Tours",
    status: "active",
    tags: ["Full-Day", "Nature", "Beach", "Wine"],
    shortDescription:
      "Explore the pristine beauty of the West Coast National Park and wine estates.",
    longDescription:
      "The West Coast National Park is a haven for nature lovers and photographers alike, offering a peaceful retreat with diverse landscapes and abundant wildlife. Whether you are interested in birdwatching, wildflower viewing, or simply enjoying the coastal scenery, a visit to this park promises a memorable experience along South Africa's west coast.\n\nCheck out the beautiful sandy white Kraalbaai Beach and the Langebaan Lagoon together, which forms a stunning natural attraction within the West Coast National Park.\n\nLastly, stop at the Groote Post winery and enjoy a leisurely lunch and taste excellent wines while immersing yourself in the history and natural beauty of the Darling region.",
    inclusions: [
      "Private transportation",
      "Entrance fee into West Coast National Park",
      "Entrance fee into Kraalbaai Beach/Lagoon",
      "Wine Tasting at the Groote Post Vineyards",
    ],
    exclusions: ["Lunch (Restaurant only opens Wed-Sun 12-2pm)", "Gratuities"],
    highlights: [
      "Visit West Coast National Park with diverse wildlife",
      "Pristine Kraalbaai Beach and Langebaan Lagoon",
      "Wine tasting at Groote Post Vineyards",
      "Coastal scenery and birdwatching opportunities",
    ],
    relatedActivities: [],
    includedActivities: [],
    optionalActivities: [],
    packageConfig: {
      description: "All entrance fees and wine tasting included",
      maxIncludedStops: 3,
      extraStopPrice: 300,
      passengerConfig: {
        minAdults: 2,
        adultPrice: 3900,
        childPrice: 1950,
        infantPrice: 0,
        ageRanges: {
          infant: "0-2 years",
          child: "2-12 years",
          adult: "12+ years",
        },
      },
    },
    standardVehicle: {
      id: "standard-tour-vehicle",
      name: "Standard Tour Vehicle",
      maxPassengers: 20,
      description: "Comfortable air-conditioned tour vehicle",
    },
    vipOptions: [
      {
        id: "mercedes-c-class",
        label: "Mercedes C-Class",
        description: "Luxury 3-seater sedan",
        maxPassengers: 3,
        price: 2000,
      },
      {
        id: "mercedes-v-class",
        label: "Mercedes V-Class",
        description: "Premium 6-seater van",
        maxPassengers: 6,
        price: 5000,
      },
    ],
    cancellationPolicy:
      "A 50% fee applies for cancellations 3 days prior; a 100% fee applies for cancellations within 24 hours.",
    route: {
      type: "fixed-itinerary",
      stops: [
        {
          id: "stop1",
          name: "Your Pickup Location",
          address: "",
          isEditable: true,
        },
        {
          id: "stop2",
          name: "West Coast National Park",
          address: "West Coast National Park, Western Cape",
          isEditable: false,
        },
        {
          id: "stop3",
          name: "Kraalbaai Beach",
          address: "West Coast National Park",
          isEditable: false,
        },
        {
          id: "stop4",
          name: "Groote Post Vineyards",
          address: "Darling, Western Cape",
          isEditable: false,
        },
        {
          id: "stop5",
          name: "Your Drop-off Location",
          address: "",
          isEditable: true,
        },
      ],
    },
    gallery: { primary: capeTownSunriseHero, additional: [] },
    audience: ["Nature Lovers", "Photographers", "Beach Enthusiasts"],
    details: {
      quickInfo: [
        { icon: "AlarmFill", label: "Duration", value: "Full Day" },
        { icon: "PeopleFill", label: "Group Size", value: "Min. 2 adults" },
      ],
    },
    pricing: {
      amount: 3900,
      type: "from",
      options: [
        { label: "Adult", price: "R3,900", description: "per person" },
        { label: "Child (2-12)", price: "R1,950", description: "per person" },
      ],
    },
  },
  {
    id: 12,
    slug: "shark-cage-diving-full-day",
    title: "Shark Cage Diving Full Day Tour",
    category: "Tours",
    status: "active",
    tags: ["Full-Day", "Adventure", "Marine Life", "Coastal"],
    shortDescription:
      "An exhilarating shark cage diving experience with coastal exploration.",
    longDescription:
      "Shark cage diving in Kleinbaai is an exhilarating experience that allows you to get up close with one of the ocean's most magnificent predators, the great white shark. The day starts off with a 2-hour drive to Kleinbaai, a small harbour town near Gansbaai, known as one of the best places in the world for shark cage diving, due to its proximity to Shark Alley, a channel between Dyer Island and Geyser Rock where great white sharks often hunt seals.\n\nAfter the dive, visit Hermanus, the whale watching capital of South Africa, followed by a scenic coastal drive back to Cape Town via Betty's Bay and Gordon's Bay.",
    inclusions: [
      "Door to Door Collection",
      "English Speaking Qualified Tour Guide",
      "Air-Conditioned Private Transport",
      "Breakfast at Shark Centre",
      "3-hour Shark Cage Dive Boat Trip",
      "Light lunch on return to shore",
      "Entrance fee into Stony Point OR Harold Porter Botanical Gardens",
    ],
    exclusions: ["Gratuities"],
    highlights: [
      "3-hour Shark Cage Dive Boat Trip",
      "Breakfast and light lunch included",
      "Visit Hermanus - whale watching capital",
      "Scenic coastal drive via Betty's Bay",
      "Optional penguin colony or botanical gardens visit",
    ],
    relatedActivities: [findActivity("shark-cage-diving")].filter(
      Boolean,
    ) as Activity[],
    includedActivities: [findActivity("hermanus-coastal-tour")].filter(
      Boolean,
    ) as Activity[],
    optionalActivities: [
      findActivity("stony-point-penguins"),
      findActivity("harold-porter-gardens"),
    ].filter(Boolean) as Activity[],
    packageConfig: {
      description:
        "All-inclusive shark diving experience with meals and coastal tour",
      maxIncludedStops: 5,
      extraStopPrice: 0,
      passengerConfig: {
        minAdults: 2,
        adultPrice: 3900,
        childPrice: 1950,
        infantPrice: 0,
        ageRanges: {
          infant: "0-2 years",
          child: "2-12 years",
          adult: "12+ years",
        },
      },
    },
    standardVehicle: {
      id: "standard-tour-vehicle",
      name: "Standard Tour Vehicle",
      maxPassengers: 20,
      description: "Comfortable air-conditioned tour vehicle",
    },
    vipOptions: [
      {
        id: "mercedes-c-class",
        label: "Mercedes C-Class",
        description: "Luxury 3-seater sedan",
        maxPassengers: 3,
        price: 2000,
      },
      {
        id: "mercedes-v-class",
        label: "Mercedes V-Class",
        description: "Premium 6-seater van",
        maxPassengers: 6,
        price: 5000,
      },
    ],
    cancellationPolicy:
      "A 50% fee applies for cancellations 3 days prior; a 100% fee applies for cancellations within 24 hours. Note: Shark cage diving is provided by an external operator and is subject to their own cancellation policies.",
    route: {
      type: "fixed-itinerary",
      stops: [
        {
          id: "stop1",
          name: "Your Pickup Location",
          address: "",
          isEditable: true,
        },
        {
          id: "stop2",
          name: "Kleinbaai Shark Centre",
          address: "Kleinbaai, Gansbaai",
          isEditable: false,
        },
        {
          id: "stop3",
          name: "Hermanus",
          address: "Hermanus, Western Cape",
          isEditable: false,
        },
        {
          id: "stop4",
          name: "Betty's Bay",
          address: "Betty's Bay, Western Cape",
          isEditable: false,
        },
        {
          id: "stop5",
          name: "Your Drop-off Location",
          address: "",
          isEditable: true,
        },
      ],
    },
    gallery: { primary: capeTownSunriseHero, additional: [] },
    audience: [
      "Adrenaline Seekers",
      "Marine Life Enthusiasts",
      "Adventurous Travelers",
    ],
    details: {
      quickInfo: [
        { icon: "AlarmFill", label: "Duration", value: "Approx. 10 Hours" },
        { icon: "PeopleFill", label: "Group Size", value: "Min. 2 adults" },
      ],
      itinerary: [
        { time: "05:00 - 07:00", title: "Hotel pick-up" },
        { title: "Breakfast on arrival at Shark Centre" },
        { title: "3-hour Shark Cage Dive Boat Trip" },
        { title: "Light Lunch at the Shark Centre" },
        {
          title: "Hermanus",
          description: "Whale watching capital of South Africa",
        },
        {
          title: "Coastal drive back to Cape Town",
          description: "Via Betty's Bay and Gordon's Bay",
        },
        {
          title:
            "Optional: Stony Point Penguin Colony OR Harold Porter Botanical Gardens",
        },
      ],
    },
    pricing: {
      amount: 3900,
      type: "from",
      options: [
        { label: "Adult", price: "R3,900", description: "per person" },
        { label: "Child (2-12)", price: "R1,950", description: "per person" },
      ],
    },
  },
];

export const serviceCategories: ServiceCategory[] = ["Transport", "Tours"];
